import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getLabel } from '../../../utils/StoreUtils'
import { getFullDate } from '../../../utils/DateUtil'
import { getResultFormat } from '../../../utils/AnalyseUtils'
import { nbPerPageLabelHuge } from '../../../referencial/constants/ReferencialConstants'
import InstallationAction from 'installation/actions/InstallationAction'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { getStationArrowNav } from 'utils/ActionUtils'
import { round } from 'lodash'
import { push } from '@lagunovsky/redux-react-router'
import ProgressCard from 'components/card/ProgressCard'
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { CardTable } from 'components/datatable/NewTable'
import { getStationTitleWithTownName } from 'utils/StationUtils'

const headers = ['date', 'parameter', 'result', 'unit', 'reference', 'producer', 'comment']

const InstallationFollowUpApp = () => {
    const dispatch = useDispatch()

    const [dataLoaded, setDataLoaded] = useState(false)
    const [startDate, setStartDate] = useState()
    const [endDate, setEndDate] = useState()

    const {
        installation,
        installationAnalysis,
        parameters,
        contributors,
        units,
        installationTable,
        citiesIndex,
    } = useSelector(store => ({
        installation: store.InstallationReducer.installation,
        installationTable: store.InstallationReducer.installationTable,
        installationAnalysis: store.InstallationReducer.installationAnalysis,
        parameters: store.ParameterReducer.parameters,
        contributors: store.ContributorReducer.contributors,
        units: store.UnitReducer.units,
        installationAnalysisCount: store.InstallationReducer.installationAnalysisCount,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const townName = useMemo(() => citiesIndex[installation.townCode]?.name, [citiesIndex, installation])

    useTitle(() => {
        return [{
            title: i18n.installation,
            href: 'installation',
        }, {
            title: getStationTitleWithTownName(installation, townName),
            href: `station/installation/${installation.id}`,
        }, {
            title: i18n.followUp,
            href: `station/installation/${installation.id}/installationFollowUp`,
        }]
    }, [installation, townName])

    useEffect(() => {
        if (installation.id) {
            dispatch(InstallationAction.fetchInstallationAnalysis(installation.id)).then(() => setDataLoaded(true))
        }
    }, [installation.id])

    const reload = (forceLoad) => {
        setDataLoaded(false)
        dispatch(InstallationAction.fetchInstallationAnalysis(installation.id, startDate, endDate, forceLoad)).then(() => setDataLoaded(true))
    }

    // componentDidMount() {
    //     if (!componentHasHabilitations(H_STATION_INSTALLATION_FOLLOW_UP)) { // A modifier quand react-router sera à jour
    //         this.props.push('/unauthorized')
    //         return
    //     }
    //     this.setExport()
    // }

    const analysisData = useMemo(() => installationAnalysis.map(a => ({
        ...a,
        date: getFullDate(a.date),
        parameter: getLabel(parameters, a.parameter, 'labelWithCode'),
        result: getResultFormat(a),
        unit: getLabel(units, a.unit, 'labelWithCode', 'id'),
        producer: getLabel(contributors, a.producer),
        comment: a.analysisComment,
    })), [installationAnalysis])

    const exportData = useMemo(() => installationAnalysis.map((a, i) => ({
        date: { value: getFullDate(a.date), format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date' },
        reference: { value: a.reference },
        parameter: { value: getLabel(parameters, a.parameter, 'labelWithCode') },
        result: { value: round(a.result, 2), format: '0.00', cellType: 'number' },
        unit: { value: getLabel(units, a.unit, 'labelWithCode', 'id') },
        producer: { value: getLabel(contributors, a.producer) },
        comment: { value: a.analysisComment },
        headers: i === 0 ? headers : [],
    })), [installationAnalysis])

    useActions(() => {
        return {
            export: () => ({
                data: exportData,
                exportType: 'xlsx',
                titleFile: `${i18n.followUp} - ${i18n.installation} - [${installation.code}] ${installation.name}`,
            }),
            arrowNav: getStationArrowNav('installation', installationTable, installation.id, s => push(`/station/installation/${s.id}/installationFollowUp`)),
        }
    })

    return dataLoaded ?
        (
            <Stack spacing={1} padding={2}>
                <Card>
                    <CardContent>
                        <Grid container spacing={1}>
                            <Grid size={5}>
                                <SimpleDatePicker
                                    label={i18n.startDate}
                                    onChange={v => setStartDate(v)}
                                    id='startDate'
                                    value={startDate}
                                    max={endDate}
                                />
                            </Grid>
                            <Grid size={5}>
                                <SimpleDatePicker
                                    label={i18n.endDate}
                                    onChange={v => setEndDate(v)}
                                    id='endDate'
                                    value={endDate}
                                    min={startDate}
                                />
                            </Grid>
                            <Grid container size={2} alignItems='center' justifyContent='flex-end'>
                                <Button onClick={reload} variant='contained'>
                                    {i18n.search}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <CardTable
                    title={i18n.dataFollowUp}
                    rows={analysisData}
                    nbPerPageLabel={nbPerPageLabelHuge}
                    headers={headers}
                />
            </Stack>

        ) : (
            <Stack padding={1}>
                <ProgressCard indeterminate />
            </Stack>
        )
}

InstallationFollowUpApp.propTypes = {}

export default InstallationFollowUpApp